/**
 * Closing a month, and the checklist that comes before it (Phase 1, step 1.7; spec v2 §27).
 *
 * A closed month stops moving: every later write to an entry inside it is refused by the
 * database, and its figures are served from a snapshot taken at close time. Reopening is the
 * exception — owner only, reason required, and it drops the snapshot so the month goes back
 * to being computed from the ledger.
 */
import { computeSafeToInvest, type SafeToInvest } from '@pfo/domain';

import { supabase } from '../../../shared/api/supabase/client.js';
import { appErrorException } from '../../../shared/lib/errors.js';

/** Codes `app.month_close_checklist` returns. Unknown codes are shown as-is, never hidden. */
export type ChecklistCode = 'RECURRING_UNCONFIRMED' | 'SUMMARY_MISSING' | 'SUMMARY_DRAFT';

export type ChecklistItem = Readonly<{ code: string; detail: string }>;

export type ClosureState = Readonly<{
  status: 'open' | 'closed';
  closedAt: string | null;
  reopenedAt: string | null;
  note: string | null;
}>;

export async function fetchChecklist(
  householdId: string,
  year: number,
  month: number,
): Promise<readonly ChecklistItem[]> {
  const { data, error } = await supabase.schema('app').rpc('month_close_checklist', {
    p_household_id: householdId,
    p_year: year,
    p_month: month,
  });
  if (error) throw appErrorException(error);
  return (data ?? []).map((row) => ({ code: row.code ?? '', detail: row.detail ?? '' }));
}

export async function fetchClosure(year: number, month: number): Promise<ClosureState | null> {
  const { data, error } = await supabase
    .from('monthly_closures')
    .select('status, closed_at, reopened_at, note')
    .eq('period_year', year)
    .eq('period_month', month)
    .maybeSingle();
  if (error) throw appErrorException(error);
  if (data === null) return null;

  return {
    status: data.status as 'open' | 'closed',
    closedAt: data.closed_at,
    reopenedAt: data.reopened_at,
    note: data.note,
  };
}

export async function closeMonth(householdId: string, year: number, month: number): Promise<void> {
  const { error } = await supabase
    .schema('app')
    .rpc('close_month', { p_household_id: householdId, p_year: year, p_month: month });
  if (error) throw appErrorException(error);
}

export async function reopenMonth(
  householdId: string,
  year: number,
  month: number,
  reason: string,
): Promise<void> {
  const { error } = await supabase.schema('app').rpc('reopen_month', {
    p_household_id: householdId,
    p_year: year,
    p_month: month,
    p_reason: reason,
  });
  if (error) throw appErrorException(error);
}

/** How far ahead "what is already owed" looks when judging what is safe to invest. */
export const SAFE_TO_INVEST_HORIZON_DAYS = 30;

/**
 * "Safe to invest" is the MONTH's own surplus (`free_cash_stable`, the Excel row "Còn lại")
 * minus a margin for unexpected spending averaged over the household's own history (AC1,
 * DBD-042). The figures come back already summed by SQL for the reference month (DBD-014);
 * the subtraction is the pure `computeSafeToInvest`, so the screen can show its working. A
 * closed month reads its snapshot, so the figure stops moving once the books are shut.
 */
export async function fetchSafeToInvest(
  subjectId: string,
  year: number,
  month: number,
): Promise<SafeToInvest> {
  const { data, error } = await supabase.schema('app').rpc('investable_capacity_inputs', {
    p_subject_id: subjectId,
    p_year: year,
    p_month: month,
    p_horizon_days: SAFE_TO_INVEST_HORIZON_DAYS,
  });
  if (error) throw appErrorException(error);

  const row = (data ?? [])[0];
  return computeSafeToInvest({
    freeCashStable: Number(row?.free_cash_stable ?? 0),
    unexpectedTotal: Number(row?.unexpected_total ?? 0),
    historyMonths: Number(row?.history_months ?? 0),
    liquidBalances: Number(row?.liquid_balances ?? 0),
    upcomingObligations: Number(row?.upcoming_obligations ?? 0),
    minCashBuffer: Number(row?.min_cash_buffer ?? 0),
  });
}

export type ReserveProgress = Readonly<{
  id: string;
  reserveType: 'emergency' | 'irregular';
  targetAmount: number | null;
  balance: number;
  /** `reserve_policies.monthly_contribution` — the pace the fund is built at (AC2). */
  monthlyContribution: number;
  accountName: string | null;
}>;

export async function fetchReserves(subjectId: string): Promise<readonly ReserveProgress[]> {
  const { data, error } = await supabase
    .from('reserve_policies')
    // prettier-ignore
    .select(
      'id, reserve_type, target_amount, monthly_contribution, linked_account_id, accounts(name)',
    )
    .eq('subject_id', subjectId)
    .eq('is_active', true);
  if (error) throw appErrorException(error);

  const rows = data ?? [];
  const accountIds = rows.flatMap((row) => (row.linked_account_id ? [row.linked_account_id] : []));
  const balances = new Map<string, number>();
  if (accountIds.length > 0) {
    const { data: balanceRows, error: balanceError } = await supabase
      .from('account_balances')
      .select('account_id, current_balance')
      .in('account_id', accountIds);
    if (balanceError) throw appErrorException(balanceError);
    for (const row of balanceRows ?? []) {
      if (row.account_id) balances.set(row.account_id, Number(row.current_balance ?? 0));
    }
  }

  return rows.map((row) => ({
    id: row.id,
    reserveType: row.reserve_type as 'emergency' | 'irregular',
    targetAmount: row.target_amount === null ? null : Number(row.target_amount),
    balance: row.linked_account_id ? (balances.get(row.linked_account_id) ?? 0) : 0,
    monthlyContribution: Number(row.monthly_contribution ?? 0),
    accountName: row.accounts?.name ?? null,
  }));
}
